import React, { useRef, useState, useEffect, useMemo } from "react";
import {
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  SearchOutlined,
  SmallDashOutlined,
  CalendarOutlined,
} from "@ant-design/icons";
import Highlighter from "react-highlight-words";
import {
  Button,
  Checkbox,
  Dropdown,
  Input,
  Menu,
  Space,
  Table,
  Tooltip,
  message,
} from "antd";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import Stack from "@mui/material/Stack";
import { Chip, Badge } from "@mui/material";
import EditNotificationsOutlinedIcon from "@mui/icons-material/EditNotificationsOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { format } from "date-fns";
import axios from "axios";
import { getFreights } from "../action/action";
import firebase from "../../../config/firebase";
import { doc, getDoc } from "firebase/firestore";
import dayjs from "dayjs";
import DatePickerMenu from "../../../components/dataPicker/dataPickerMenu";
import DialogFileDriver from "../../../components/dialog-fila-driver/dialog-fila-driver";
import ModalFilter from "../../../components/ModalFilter/ModalFilter";
import YesOrNot from "./yesOrNot";
import { render } from "@testing-library/react";

// ========================
// Config axios (opcional)
// ========================
const api = axios.create({
  baseURL:
    process.env.REACT_APP_FROT_API_URL ||
    "https://api-core-comunicacao-git-631554619185.europe-west1.run.app/api",
  timeout: 20000,
});

const menuItemStyle = {
  height: 44,
  borderRadius: 8,
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "0 14px",
  color: "#012442",
  fontWeight: 500,
};

// Se você usa JWT, descomente e ajuste:
// api.interceptors.request.use((config) => {
//   const token = localStorage.getItem('token');
//   if (token) config.headers.Authorization = `Bearer ${token}`;
//   return config;
// });

/**
 * Mapeia o retorno do backend para o formato que a tabela já espera.
 * Ajuste os caminhos conforme o JSON real da sua API.
 */
function mapFreightFromApi(item) {
  // Exemplos de “formatos possíveis” do backend:
  // A) item já vem no shape atual (clientPayment, firstDelivery, etc.)
  // B) item vem normalizado (client, origin, destination...)
  //
  // Aqui a gente suporta os dois, caindo em fallback.



  const id = item.id || item._id;

  const clientPayment = item.clientPayment || {
    uid: item.client?.uid,
    name: item.client?.name,
    cnpj: item.client?.cnpj,
  };

  const freight = item.freight || {
    valueFreightage: item.valueFreightage ?? item.freightValue ?? 0,
    getDriverFreight: item.freight?.getDriverFreight || {
      uidDriver: item.driver?.uid,
      name: item.driver?.name,
    },
  };

  const driver = item.driver || {
    valueNegotiated:
      item.paymentToDriver ??
      item.driverValueNegotiated ??
      item.driver?.valueNegotiated ??
      0,
    valueInitial: item.driver?.valueInitial ?? 0,
    valueFinal: item.driver?.valueFinal ?? 0,
    paymentCondition: item.driver?.paymentCondition,
  };

  const firstDelivery = item.firstDelivery || {
    city: item.origin?.city,
    address: item.origin?.address,
    cep: item.origin?.cep,
    dateColeta: item.origin?.dateColeta || item.dateColeta,
    coordinates: item.origin?.coordinates,
    name: item.origin?.name,
    uf: item.origin?.uf,
  };

  const lastDelivery = item.lastDelivery || {
    city: item.destination?.city,
    address: item.destination?.address,
    cep: item.destination?.cep,
    coordinates: item.destination?.coordinates,
    name: item.destination?.name,
  };

  const status = item.status || {
    describe: item.statusDescribe || item.status_text || "",
  };

  return {
    id,
    numberSerial: item.numberSerial ?? item.code ?? item.serial,
    clientPayment,
    freight,
    driver,
    firstDelivery,
    lastDelivery,
    status,
  };
}

function moneyToInt(value) {
  if (value == null) return 0;
  if (typeof value === "number") return Math.round(value);
  const str = String(value);
  return Math.round(Number(str.replace(/\./g, "").replace(",", ".")));
}

// Normaliza qualquer valor de data para Date (somente dia/mês/ano)
function getDateOnly(value) {
  if (!value) return null;

  if (typeof value === "string") {
    const [year, month, day] = value.split("-").map(Number);
    if (!year || !month || !day) return null;
    return new Date(year, month - 1, day);
  }

  if (value instanceof Date) {
    if (isNaN(value.getTime())) return null;
    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }

  // dayjs/moment etc
  if (typeof value === "object" && typeof value.toDate === "function") {
    const d = value.toDate();
    if (!(d instanceof Date) || isNaN(d.getTime())) return null;
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }

  return null;
}

const normalizeCityUf = (value = "") =>
  String(value)
    .toLowerCase()
    .replace(/\s+/g, "")
    .replace(/-/g, "");

function renderStatusColor(status) {
  const chipStyle = { minWidth: "100px", maxWidth: "150px" };
  const commonChip = (label, color) => (
    <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
      <Chip
        label={label}
        sx={{
          ...chipStyle,
          backgroundColor: color,
          color: "#F9FAFB",
          borderRadius: "6px",
          fontSize: "12px",
          fontWeight: 600,
          height: 28,
          px: 0.5,
          boxShadow:
            "0px 3px 3px rgba(0,14,51,0.25), 0px 1px 2px rgba(0,14,51,0.25)",
          border: "1px solid rgba(1,36,66,0.08)",
          "& .MuiChip-label": {
            px: 1,
          },
        }}
      />
    </Stack>
  );

  switch (status) {
    case "Contratado":
      return commonChip(status, "#059669");
    case "P.Contratação":
    case "Pendente de contratação":
      return commonChip("P.Contratação", "#F97316");
    case "Em transito":
    case "Em trânsito":
      return commonChip("Em trânsito", "#16A34A");
    case "Entregue":
      return commonChip(status, "#2563EB");
    case "Finalizado":
      return commonChip(status, "#6B7280");
    case "Deslocando-se para a coleta":
      return commonChip(status, "#22C55E");
    case "Aguardando Carregamento":
      return commonChip(status, "#F59E0B");
    case "Aguardando Documentação":
      return commonChip(status, "#FBBF24");
    case "Aguardando Descarregamento":
      return commonChip(status, "#EAB308");
    default:
      return commonChip(status || "—", "#020202");
  }
}

// ========================
// Badge (API-only)
// ========================
const DriverBadge = ({ recordId }) => {
  const [count, setCount] = useState(0);
  const [status, setStatus] = useState("");

  useEffect(() => {
    let mounted = true;

    async function load() {
      try {
        // 1) status do frete (se você já tiver no payload principal, pode remover esta chamada)
        const freightRes = await api.get(`/freight/${recordId}`);
        const describe =
          freightRes?.data?.status?.describe ??
          freightRes?.data?.statusDescribe ??
          "";
        if (mounted) setStatus(describe);

        // 2) tamanho da fila
        const queueRes = await api.get(`/freight/${recordId}/queue`);
        const queue = Array.isArray(queueRes.data)
          ? queueRes.data
          : queueRes.data?.items || [];
        if (mounted) setCount(queue.length);
      } catch (e) {
        // silencioso para não poluir
      }
    }

    load();
    return () => {
      mounted = false;
    };
  }, [recordId]);

  // Regra original: se Finalizado ou Contratado, não mostra badge/click de fila.
  if (status === "Finalizado" || status === "Contratado") {
    return <EditNotificationsOutlinedIcon />;
  }

  return (
    <Link to={"#" + recordId}>
      {count > 0 ? (
        <Badge badgeContent={count} color="success">
          <EditNotificationsOutlinedIcon color="primary" />
        </Badge>
      ) : (
        <EditNotificationsOutlinedIcon color="primary" />
      )}
    </Link>
  );
};

// ========================
// Componente principal
// ========================
const DataFreteTableCopy = ({
  cleanFilter,
  openFilter,
  onCleanFilterHandled,
  onOpenFilterHandled,
}) => {
  const user = useSelector((state) => state.user);
  const db = firebase.firestore();

  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const [openModalFilter, setOpenModalFilter] = useState(false);
  const [driversInFila, setDriversInFila] = useState([]);
  const [datas, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  const [dateRange, setDateRange] = useState([]);
  const hasLoadedRef = useRef(false);

  const [columnFilters, setColumnFilters] = useState({});
  const [statusSearchText, setStatusSearchText] = useState("");
  const [sorterConfig, setSorterConfig] = useState({
    columnKey: null,
    order: null,
  });

  // Estados de paginação
  const [current, setCurrent] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const searchInput = useRef(null);
  const statusFilterRef = useRef(null);

  const storageKey = (uid, key) => `${uid}:${key}`;

  const handleDateRangeChange = (dates) => {
    setDateRange(dates ? [dates[0], dates[1]] : []);
  };

  // ========================
  // Fetch API (somente API)
  // ========================
  const fetchData = async () => {
    setLoading(true);
    try {
      /**
       * IMPORTANTE:
       * - Não envie uids/viewALL pelo front como "regra de segurança".
       * - O backend deve usar o usuário do token e aplicar a regra.
       *
       * Se você quiser mandar só pra “debug”, mande e o backend IGNORA se não tiver permissão.
       */
      const params = {};
      if (user?.uidShipper) params.uidShipper = user.uidShipper;
      if (
        Array.isArray(user?.uidShipperAffiliates) &&
        user.uidShipperAffiliates.length
      ) {
        params.affiliates = user.uidShipperAffiliates.join(",");
      }
      if (typeof user?.viewALL === "boolean")
        params.viewAll = String(user.viewALL);

      const payload = await getFreights(params);
      const items = Array.isArray(payload?.data)
        ? payload.data
        : payload?.data?.items || [];
      const mapped = items.map(mapFreightFromApi);

      const enriched = await Promise.all(
        mapped.map(async (item) => {
          const uid = item?.freight?.getDriverFreight?.uidDriver;
          const existingName = item?.freight?.getDriverFreight?.name;

          // Busca a subcoleção stopping_points do Firestore
          let stoppingPoints = [];
          try {
            const stoppingPointsSnapshot = await db
              .collection("freight")
              .doc(item.id)
              .collection("stopping_points")
              .get();
            stoppingPoints = stoppingPointsSnapshot.docs.map((spDoc) => ({
              id: spDoc.id,
              ...spDoc.data(),
            }));
          } catch (error) {
            console.error("Erro ao buscar stopping_points:", error);
          }

          // Se não tiver uidDriver ou já tiver nome, retorna com stopping_points
          if (!uid || existingName) {
            return {
              ...item,
              stopping_points: stoppingPoints,
            };
          }

          // Busca o nome do motorista no Firestore
          try {
            const driverRef = doc(db, "drivers_users", uid);
            const driverSnap = await getDoc(driverRef);

            if (driverSnap.exists()) {
              const driverData = driverSnap.data();
              return {
                ...item,
                stopping_points: stoppingPoints,
                freight: {
                  ...item.freight,
                  getDriverFreight: {
                    ...item.freight.getDriverFreight,
                    name: driverData.name || "Nome não encontrado",
                  },
                },
              };
            }

            return {
              ...item,
              stopping_points: stoppingPoints,
              freight: {
                ...item.freight,
                getDriverFreight: {
                  ...item.freight.getDriverFreight,
                  name: "Motorista não encontrado",
                },
              },
            };
          } catch (error) {
            return {
              ...item,
              stopping_points: stoppingPoints,
              freight: {
                ...item.freight,
                getDriverFreight: {
                  ...item.freight.getDriverFreight,
                  name: "Erro ao buscar nome",
                },
              },
            };
          }
        }),
      );
      setData(enriched);
    } catch (err) {
      message.error("Erro ao buscar fretes na API");
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid]);

  // ========================
  // localStorage filtros
  // ========================
  useEffect(() => {
    if (!user?.uid) return;

    try {
      const savedDateRange = localStorage.getItem(
        storageKey(user.uid, "freightDateRangeFilter"),
      );
      const savedColumnFilters = localStorage.getItem(
        storageKey(user.uid, "freightColumnFilters"),
      );
      const savedSorter = localStorage.getItem(
        storageKey(user.uid, "freightTableSorter"),
      );

      // Carrega os filtros de coluna
      if (savedColumnFilters) {
        const parsedFilters = JSON.parse(savedColumnFilters);
        if (parsedFilters && typeof parsedFilters === "object") {
          setColumnFilters(parsedFilters);
        } else {
          setColumnFilters({});
        }
      } else {
        setColumnFilters({});
      }

      if (savedSorter) {
        const parsedSorter = JSON.parse(savedSorter);
        if (
          parsedSorter &&
          typeof parsedSorter === "object" &&
          typeof parsedSorter.columnKey === "string" &&
          (parsedSorter.order === "ascend" || parsedSorter.order === "descend")
        ) {
          setSorterConfig({
            columnKey: parsedSorter.columnKey,
            order: parsedSorter.order,
          });
        } else {
          setSorterConfig({ columnKey: null, order: null });
        }
      } else {
        setSorterConfig({ columnKey: null, order: null });
      }

      // Carrega o filtro de data e converte strings para dayjs
      if (savedDateRange) {
        const parsedDateRange = JSON.parse(savedDateRange);
        if (
          Array.isArray(parsedDateRange) &&
          parsedDateRange.length === 2 &&
          parsedDateRange[0] &&
          parsedDateRange[1]
        ) {
          // Converte strings ISO para objetos dayjs
          const startDate = dayjs(parsedDateRange[0]);
          const endDate = dayjs(parsedDateRange[1]);

          // Verifica se as datas são válidas
          if (startDate.isValid() && endDate.isValid()) {
            setDateRange([startDate, endDate]);
            setSearchedColumn("dateColeta");
          } else {
            setDateRange([]);
          }
        } else {
          setDateRange([]);
        }
      } else {
        setDateRange([]);
      }
    } catch (error) {
      console.error("Erro ao carregar filtros do localStorage:", error);
      setColumnFilters({});
      setDateRange([]);
      setSorterConfig({ columnKey: null, order: null });
    }

    hasLoadedRef.current = true;
  }, [user?.uid]);

  useEffect(() => {
    if (!hasLoadedRef.current) return;
    if (!user?.uid) return;

    // Verifica se dateRange é um array com elementos válidos
    if (
      dateRange &&
      Array.isArray(dateRange) &&
      dateRange.length > 0 &&
      dateRange[0] &&
      dateRange[1]
    ) {
      localStorage.setItem(
        storageKey(user.uid, "freightDateRangeFilter"),
        JSON.stringify(dateRange),
      );
    } else {
      localStorage.removeItem(storageKey(user.uid, "freightDateRangeFilter"));
    }

    // Verifica se columnFilters tem pelo menos uma chave
    if (columnFilters && Object.keys(columnFilters).length > 0) {
      localStorage.setItem(
        storageKey(user.uid, "freightColumnFilters"),
        JSON.stringify(columnFilters),
      );
    } else {
      localStorage.removeItem(storageKey(user.uid, "freightColumnFilters"));
    }

    if (sorterConfig?.columnKey && sorterConfig?.order) {
      localStorage.setItem(
        storageKey(user.uid, "freightTableSorter"),
        JSON.stringify(sorterConfig),
      );
    } else {
      localStorage.removeItem(storageKey(user.uid, "freightTableSorter"));
    }
  }, [user?.uid, dateRange, columnFilters, sorterConfig]);

  // ========================
  // Limpar filtros via prop externa
  // ========================
  useEffect(() => {
    if (cleanFilter) {
      setColumnFilters({});
      setDateRange([]);
      setSearchText("");
      setSearchedColumn("");
      setStatusSearchText("");
      setSorterConfig({ columnKey: null, order: null });
      if (user?.uid) {
        localStorage.removeItem(storageKey(user.uid, "freightDateRangeFilter"));
        localStorage.removeItem(storageKey(user.uid, "freightColumnFilters"));
        localStorage.removeItem(storageKey(user.uid, "freightTableSorter"));
      }
      if (onCleanFilterHandled) {
        onCleanFilterHandled();
      }
    }
  }, [cleanFilter, user?.uid, onCleanFilterHandled]);

  // ========================
  // Abrir modal de filtros salvos via prop externa
  // ========================
  useEffect(() => {
    if (openFilter) {
      setOpenModalFilter(true);
      if (onOpenFilterHandled) {
        onOpenFilterHandled();
      }
    }
  }, [openFilter, onOpenFilterHandled]);

  // ========================
  // Modal fila (API-only)
  // ========================
  const handleOpen = () => setOpenModal(true);

  const handleClose = () => {
    setOpenModal(false);
    setDriversInFila([]);
  };

  const handleApplyCustomFilter = (filter) => {
    // Limpar filtros existentes antes de aplicar o novo
    const newFilters = {};
    let newDateRange = null;

    // Processar cada condição do filtro
    if (filter.conditions && filter.conditions.length > 0) {
      filter.conditions.forEach((condition) => {
        const { column, operator, value } = condition;

        // Tratar filtros de data separadamente
        if (column === "firstDelivery.dateColeta") {
          if (
            operator === "between" &&
            value &&
            Array.isArray(value) &&
            value.length === 2
          ) {
            const startDate =
              typeof value[0] === "string" ? dayjs(value[0]) : value[0];
            const endDate =
              typeof value[1] === "string" ? dayjs(value[1]) : value[1];
            newDateRange = [startDate, endDate];
          } else if (operator === "equals" && value) {
            const date = typeof value === "string" ? dayjs(value) : value;
            newDateRange = [date, date];
          } else if (operator === "after" && value) {
            const date = typeof value === "string" ? dayjs(value) : value;
            newDateRange = [date, null];
          } else if (operator === "before" && value) {
            const date = typeof value === "string" ? dayjs(value) : value;
            newDateRange = [null, date];
          } else if (value) {
            const date = typeof value === "string" ? dayjs(value) : value;
            newDateRange = [date, date];
          }
        }

        // Tratar filtros de status
        else if (column === "status.describe") {
          if (operator === "in" && Array.isArray(value) && value.length > 0) {
            newFilters[column] = value;
          } else if (operator === "equals" && value) {
            newFilters[column] = Array.isArray(value) ? value : [value];
          }
        }
        // Tratar filtros numéricos com operadores condicionais
        else if (
          (column === "freight.valueFreightage" ||
            column === "driver.valueNegotiated") &&
          value
        ) {
          newFilters[column] = {
            operator,
            value: parseFloat(value) || 0,
          };
        }
        // Tratar outros filtros de texto/número, Para operador 'contains' ou 'equals' em campos de texto
        else {
          if (
            operator === "contains" ||
            operator === "equals" ||
            operator === "startsWith" ||
            operator === "endsWith"
          ) {
            newFilters[column] = value;
          }
        }
      });
    }

    // Aplicar os filtros de forma síncrona, primeiro aplicar o dateRange, depois os columnFilters
    if (newDateRange) {
      setDateRange(newDateRange);
    }

    // Aplicar os filtros de coluna, bug encontrado de column + dateRange por isso essa ordem
    setColumnFilters(newFilters);
  };

  // Data de coleta: prioriza menor date_operation de stopping_points, fallback para firstDelivery
  const getDateColetaFromRecord = React.useCallback((record) => {
    console.log(record);
    const minFromStopping = getMinDateOperation(record?.stopping_points);
    return minFromStopping || record?.firstDelivery?.dateColeta;
  }, []);

  const getDriversQueue = async (id) => {
    try {
      const res = await api.get(`/freight/${id}/queue`);
      const queue = Array.isArray(res.data) ? res.data : res.data?.items || [];

      console.log(res);
      debugger
      setDriversInFila(queue);
      handleOpen();
    } catch (e) {
      message.error("Erro ao buscar fila do frete");
    }
  };

  // ========================
  // Delete (API-only)
  // ========================

  const [open, setOpen] = React.useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const handleCloseYesOrNot = () => {
    setOpen(false);
    setPendingDeleteId(null);
  };
  const handleOpenYesOrNot = () => setOpen(true);

  const handleModalResult = async (value) => {
    if (!value || !pendingDeleteId) return;
    console.log(`CONFIRMADO para o passo: ${value}`);
    try {
      await api.delete(`/freight/delete/${pendingDeleteId}`);
      message.success("Frete exclu??do com sucesso!");
      fetchData();
    } catch (error) {
      message.error("Erro ao excluir o frete");
    } finally {
      setPendingDeleteId(null);
    }
  };

  const deleteEmb = (id) => {
    setPendingDeleteId(id);
    handleOpenYesOrNot();
  };

  // ========================
  // Filtros AntD (igual ao seu)
  // ========================
  const handleSearch = (selectedKeys, confirm, columnKey) => {
    debugger
    setSearchText(selectedKeys[0]?.toString() ?? "");
    confirm();
    setColumnFilters((prev) => ({
      ...prev,
      [columnKey]:
        columnKey === "freight.valueFreightage" ||
          columnKey === "driver.valueNegotiated"
          ? moneyToInt(selectedKeys)
          : (selectedKeys.toString() ?? ""),
    }));
  };

  const handleSearchDate = (confirm) => {
    confirm();
    setSearchText("");
    setSearchedColumn("dateColeta");
  };

  const handleReset = (clearFilters) => {
    clearFilters();
    setColumnFilters((prev) => {
      const updatedFilters = { ...prev };
      delete updatedFilters[searchedColumn];
      return updatedFilters;
    });
  };

  const handleResetDate = (clearFilters) => {
    clearFilters();
    setDateRange([]);
  };

  const getColumnSearchProps = (dataIndex, type = "text", maxLength = 40) => {
    const columnKey = Array.isArray(dataIndex)
      ? dataIndex.join(".")
      : dataIndex;

    return {
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys = [],
        confirm,
        clearFilters,
        close,
      }) => (
        <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
          <Input
            ref={searchInput}
            placeholder={`Buscar ${columnKey}`}
            value={selectedKeys[0] ?? ""}
            maxLength={type === "text" ? maxLength : undefined}
            onChange={(e) => {
              let value = e.target.value ?? "";
              if (type === "number") {
                value = value.replace(/\D/g, "");
              } else if (type === "money") {
                value = value.replace(/\D/g, "");
                value = (Number(value) / 100).toLocaleString("pt-BR", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                });
              } else if (type === "text") {
                try {
                  value = value.replace(/[0-9]/g, "");
                } catch {
                  value = value.replace(/\d/g, "");
                }
              }
              setSelectedKeys(value ? [value] : []);
            }}
            onPressEnter={() => handleSearch(selectedKeys, confirm, columnKey)}
            style={{ marginBottom: 8, display: "block" }}
          />

          <Space>
            <Button
              type="primary"
              onClick={() => handleSearch(selectedKeys, confirm, columnKey)}
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
            >
              Buscar
            </Button>

            <Button
              onClick={() => {
                if (clearFilters) handleReset(clearFilters);

                columnFilters[columnKey] &&
                  setColumnFilters((prev) => {
                    const updatedFilters = { ...prev };
                    delete updatedFilters[columnKey];
                    return updatedFilters;
                  });

                confirm();
              }}
              size="small"
              style={{ width: 90 }}
            >
              Reiniciar
            </Button>

            <Button type="link" size="small" onClick={() => close()}>
              Fechar
            </Button>
          </Space>
        </div>
      ),

      filterIcon: (filtered) => (
        <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
      ),

      onFilter: (value, record) => {


        debugger
        if (value == null || value === "") return true;

        const dataIndexArray = Array.isArray(dataIndex)
          ? dataIndex
          : [dataIndex];
        let recordValue = record;

        console.log({
          valueOriginal: value,
          valueTratado: value?.toString().toLowerCase().replace(/\s*-\s*.*$/, ""),
          recordValue,
          recordValueTratado: recordValue?.toString().toLowerCase(),
        });

        for (const index of dataIndexArray) recordValue = recordValue?.[index];

        if (recordValue == null) return false;

        if (type === "number")
          return String(recordValue).includes(String(value));

        return recordValue
          .toString()
          .toLowerCase()
          .includes(value.toString().toLowerCase().replace(/\s*-\s*.*$/, ""));
      },

      onFilterDropdownOpenChange: (visible) => {
        if (visible) setTimeout(() => searchInput.current?.select(), 100);
      },

      render: (text) =>
        searchedColumn === columnKey ? (
          <Highlighter
            highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
            searchWords={[searchText]}
            autoEscape
            textToHighlight={text ? text.toString() : ""}
          />
        ) : (
          text
        ),
    };
  };

  const getColumnSearchPropsDate = () => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <DatePickerMenu
          value={dateRange}
          onChange={(dates) => {
            handleDateRangeChange(dates);
            setSelectedKeys(dates ? [dates] : []);
          }}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearchDate(confirm)}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Buscar
          </Button>
          <Button
            onClick={() => clearFilters && handleResetDate(clearFilters)}
            size="small"
            style={{ width: 90 }}
          >
            Reiniciar
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <CalendarOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
    ),
    onFilter: (_, record) => {
      if (!dateRange || !dateRange.length) return true;

      const [startDate, endDate] = dateRange;

      const dateColeta = getDateOnly(record.firstDelivery?.dateColeta);
      if (!dateColeta) return false;

      const start = getDateOnly(startDate);
      const end = getDateOnly(endDate);

      if (start && end && start.getTime() === end.getTime())
        return dateColeta.getTime() === start.getTime();
      if (start && !end) return dateColeta.getTime() >= start.getTime();
      if (start && end)
        return (
          dateColeta.getTime() >= start.getTime() &&
          dateColeta.getTime() <= end.getTime()
        );

      return true;
    },
    onFilterDropdownOpenChange: (visible) => {
      if (visible) setTimeout(() => searchInput.current?.select(), 100);
    },
    render: (text) =>
      searchedColumn === "dateColeta" ? (
        <Highlighter
          highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text
      ),
  });

  // Retorna a menor date_operation do array stopping_points (formato 'YYYY-MM-DD')
  const getMinDateOperation = (stoppingPoints) => {
    if (
      !stoppingPoints ||
      !Array.isArray(stoppingPoints) ||
      stoppingPoints.length === 0
    )
      return null;
    const dates = stoppingPoints
      .map((sp) => sp?.date_operation)
      .filter(Boolean);
    if (dates.length === 0) return null;
    return dates.sort()[0]; // 'YYYY-MM-DD' ordena corretamente como string
  };

  // Status filter (multi checkbox) igual ao seu
  const getFilteredDataWithoutStatus = () => {
    let filteredData = [...datas];

    if (dateRange && dateRange.length) {
      const [startDate, endDate] = dateRange;
      filteredData = filteredData.filter((record) => {
        const dateColeta = getDateOnly(record.firstDelivery?.dateColeta);
        if (!dateColeta) return false;

        const start = getDateOnly(startDate);
        const end = getDateOnly(endDate);

        if (start && end && start.getTime() === end.getTime())
          return dateColeta.getTime() === start.getTime();
        if (start && !end) return dateColeta.getTime() >= start.getTime();
        if (start && end)
          return (
            dateColeta.getTime() >= start.getTime() &&
            dateColeta.getTime() <= end.getTime()
          );

        return true;
      });
    }

    Object.keys(columnFilters).forEach((columnKey) => {
      if (columnKey === "status.describe") return;

      const filterValue = columnFilters[columnKey];
      if (filterValue == null || filterValue === "") return;

      filteredData = filteredData.filter((record) => {
        if (columnKey === "dateColeta") return true;

        const columnKeyArray = columnKey.split(".");
        let recordValue = record;

        for (const key of columnKeyArray) recordValue = recordValue?.[key];

        if (recordValue == null) return false;

        const isNumber = columnKey === "numberSerial";
        const isMoney =
          columnKey === "freight.valueFreightage" ||
          columnKey === "driver.valueNegotiated";

        if (isNumber) return String(recordValue).includes(String(filterValue));

        if (isMoney) {
          const filterInt =
            typeof filterValue === "number"
              ? filterValue
              : moneyToInt(filterValue);
          const recordInt =
            typeof recordValue === "number"
              ? Math.round(recordValue)
              : moneyToInt(recordValue);
          return String(recordInt).includes(String(filterInt));
        }

        return recordValue
          .toString()
          .toLowerCase()
          .includes(filterValue.toString().toLowerCase());
      });
    });

    return filteredData;
  };

  const getUniqueStatusValues = () => {
    const filteredData = getFilteredDataWithoutStatus();
    const uniqueStatuses = new Set();
    filteredData.forEach((record) => {
      if (record.status?.describe) uniqueStatuses.add(record.status.describe);
    });
    return Array.from(uniqueStatuses).sort();
  };

  const getStatusFilterProps = () => {
    const statusOptions = getUniqueStatusValues();

    return {
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys = [],
        confirm,
        clearFilters,
      }) => {
        const filteredOptions = statusSearchText
          ? statusOptions.filter((status) =>
            status.toLowerCase().includes(statusSearchText.toLowerCase()),
          )
          : statusOptions;

        const handleSelectAll = (checked) => {
          if (checked) setSelectedKeys(filteredOptions);
          else setSelectedKeys([]);
        };

        const handleChange = (status, checked) => {
          if (checked) setSelectedKeys([...selectedKeys, status]);
          else setSelectedKeys(selectedKeys.filter((key) => key !== status));
        };

        const allSelected =
          filteredOptions.length > 0 &&
          filteredOptions.every((option) => selectedKeys.includes(option));
        const someSelected =
          filteredOptions.some((option) => selectedKeys.includes(option)) &&
          !allSelected;

        return (
          <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
            <Input
              ref={statusFilterRef}
              placeholder="Buscar..."
              value={statusSearchText}
              onChange={(e) => setStatusSearchText(e.target.value)}
              style={{ marginBottom: 8 }}
              allowClear
              onPressEnter={() => confirm()}
            />

            <div style={{ maxHeight: 250, overflowY: "auto", marginBottom: 8 }}>
              <div style={{ marginBottom: 4 }}>
                <Checkbox
                  indeterminate={someSelected}
                  checked={allSelected}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                >
                  (Selecionar todos)
                </Checkbox>
              </div>

              {filteredOptions.map((status) => (
                <div key={status} style={{ marginBottom: 4 }}>
                  <Checkbox
                    checked={selectedKeys.includes(status)}
                    onChange={(e) => handleChange(status, e.target.checked)}
                  >
                    {status === "Pendente de contratação"
                      ? "P.Contratação"
                      : status}
                  </Checkbox>
                </div>
              ))}
            </div>

            <Space>
              <Button
                onClick={() => {
                  if (clearFilters) clearFilters();
                  setSelectedKeys([]);
                  setStatusSearchText("");
                  setColumnFilters((prev) => {
                    const updated = { ...prev };
                    delete updated["status.describe"];
                    return updated;
                  });
                  confirm();
                }}
                size="small"
                style={{ width: 90 }}
              >
                Resetar
              </Button>

              <Button
                type="primary"
                onClick={() => {
                  setColumnFilters((prev) => ({
                    ...prev,
                    "status.describe": selectedKeys,
                  }));
                  confirm();
                }}
                size="small"
                style={{ width: 90 }}
              >
                Aplicar
              </Button>
            </Space>
          </div>
        );
      },
      filterSearch: true,
      filterMultiple: true,
      onFilter: (value, record) => {
        const filterValues = columnFilters["status.describe"];
        if (
          !filterValues ||
          !Array.isArray(filterValues) ||
          filterValues.length === 0
        )
          return true;
        return filterValues.includes(record.status?.describe);
      },
      onFilterDropdownOpenChange: (visible) => {
        if (visible) {
          setTimeout(() => statusFilterRef.current?.select(), 100);
          setStatusSearchText("");
        }
      },
    };
  };

  // Ordenação por prioridade (igual à sua ideia)
  const statusPriority = useMemo(
    () => ({
      Entregue: 1,
      "Em trânsito": 2,
      Contratado: 3,
      "Pendente de contratação": 4,
      Finalizado: 5,
    }),
    [],
  );

  const columns = [
    {
      title: "Data Coleta",
      dataIndex: ["firstDelivery", "dateColeta"],
      key: "dateColeta",
      align: "center",
      sortOrder:
        sorterConfig.columnKey === "dateColeta" ? sorterConfig.order : null,
      filteredValue: dateRange && dateRange.length ? [dateRange] : null,
      ...getColumnSearchPropsDate(),
      sorter: (a, b) => {
        const dateA = a.firstDelivery?.dateColeta;
        const dateB = b.firstDelivery?.dateColeta;

        if (!dateA) return -1;
        if (!dateB) return 1;

        const [yearA, monthA, dayA] = dateA.split("-").map(Number);
        const [yearB, monthB, dayB] = dateB.split("-").map(Number);

        return (
          new Date(yearA, monthA - 1, dayA).getTime() -
          new Date(yearB, monthB - 1, dayB).getTime()
        );
      },
      render: (dataColeta) => {
        if (dataColeta && dataColeta !== "") {
          const [year, month, day] = dataColeta.split("-");
          const localDate = new Date(+year, +month - 1, +day);
          return format(localDate, "dd/MM/yyyy");
        }
        return "";
      },
    },
    {
      title: "Código Frete",
      dataIndex: "numberSerial",
      key: "numberSerial",
      align: "center",
      sortOrder:
        sorterConfig.columnKey === "numberSerial" ? sorterConfig.order : null,
      filteredValue: columnFilters["numberSerial"]
        ? [columnFilters["numberSerial"]]
        : null,
      ...getColumnSearchProps("numberSerial", "number"),
      sorter: (a, b) =>
        (a.numberSerial?.toString() || "").localeCompare(
          b.numberSerial?.toString() || "",
        ),
      render: (value) => <span>{value}</span>,
    },
    {
      title: "Nome Pagador",
      dataIndex: ["clientPayment", "name"],
      key: "nameClient",
      align: "center",
      sortOrder:
        sorterConfig.columnKey === "nameClient" ? sorterConfig.order : null,
      filteredValue: columnFilters["clientPayment.name"]
        ? [columnFilters["clientPayment.name"]]
        : null,
      ...getColumnSearchProps(["clientPayment", "name"], "text", 40),
      sorter: (a, b) =>
        (a.clientPayment?.name || "").localeCompare(
          b.clientPayment?.name || "",
        ),
    },
    {
      title: "Nome Motorista",
      dataIndex: ["freight", "getDriverFreight", "name"],
      key: "nameDriver", // camelCase correto
      align: "center",
      sortOrder:
        sorterConfig.columnKey === "nameDriver" ? sorterConfig.order : null,
      filteredValue: columnFilters["freight.getDriverFreight.name"]
        ? [columnFilters["freight.getDriverFreight.name"]]
        : null,
      ...getColumnSearchProps(
        ["freight", "getDriverFreight", "name"],
        "text",
        40,
      ),
      sorter: (a, b) => {
        const nameA = a.freight?.getDriverFreight?.name || "";
        const nameB = b.freight?.getDriverFreight?.name || "";
        return nameA.localeCompare(nameB);
      },
    },
    {
      title: "Origem",
      dataIndex: ["firstDelivery", "city"],
      key: "firstDeliveryCity",
      // align: "center",
      sortOrder:
        sorterConfig.columnKey === "firstDeliveryCity"
          ? sorterConfig.order
          : null,
      filteredValue: columnFilters["firstDelivery.city"]
        ? [columnFilters["firstDelivery.city"]]
        : null,
      ...getColumnSearchProps(["firstDelivery", "city"], "text", 40),
      onFilter: (value, record) => {
        if (!value) return true;

        const city = record?.firstDelivery?.city || "";
        const uf = record?.firstDelivery?.uf || "";

        const haystack = `${city} ${uf} ${city} - ${uf}`
          .toLowerCase()
          .replace(/\s+/g, " ")
          .trim();

        const search = String(value)
          .toLowerCase()
          .replace(/\s+/g, " ")
          .trim();

        return haystack.includes(search);
      },
      render: (_, record) => (
        <span>
          {record?.firstDelivery?.city || ""}{" "}
          {record?.firstDelivery?.uf
            ? `- ${record.firstDelivery.uf}`
            : ""}
        </span>
      ),
      sorter: (a, b) =>
        (a.firstDelivery?.city || "").localeCompare(
          b.firstDelivery?.city || "",
        ),
    },
    {
      title: "Destino",
      dataIndex: ["lastDelivery", "city"],
      key: "lastDeliveryCity",
      align: "center",
      sortOrder:
        sorterConfig.columnKey === "lastDeliveryCity"
          ? sorterConfig.order
          : null,
      filteredValue: columnFilters["lastDelivery.city"]
        ? [columnFilters["lastDelivery.city"]]
        : null,
      ...getColumnSearchProps(["lastDelivery", "city"], "text", 40),
      onFilter: (value, record) => {
        const city = record?.firstDelivery?.city || "";
        const uf = record?.firstDelivery?.uf || "";
        const hay = `${city} - ${uf}`.toLowerCase().replace(/\s+/g, "");
        const needle = String(value).toLowerCase().replace(/\s+/g, "");
        return hay.includes(needle);
      },
      render: (_, record) => (
        <span>{record?.lastDelivery?.city || ""} - {record?.lastDelivery?.uf || ""}</span>
      ),
      sorter: (a, b) =>
        (a.lastDelivery?.city || "").localeCompare(b.lastDelivery?.city || ""),

    },
    {
      title: "Valor do Frete",
      dataIndex: ["freight", "valueFreightage"],
      key: "freight",
      align: "center",
      sortOrder:
        sorterConfig.columnKey === "freight" ? sorterConfig.order : null,
      filteredValue: columnFilters["freight.valueFreightage"]
        ? [columnFilters["freight.valueFreightage"]]
        : null,
      ...getColumnSearchProps(["freight", "valueFreightage"], "money"),
      render: (valueFreightage) =>
        `${(valueFreightage || 0).toLocaleString("pt-br", { style: "currency", currency: "BRL" })}`,
      sorter: (a, b) =>
        (a.freight?.valueFreightage || 0) - (b.freight?.valueFreightage || 0),
    },
    {
      title: "Pagando ao Motorista",
      dataIndex: ["driver", "valueNegotiated"],
      key: "valueNegotiated",
      align: "center",
      sortOrder:
        sorterConfig.columnKey === "valueNegotiated"
          ? sorterConfig.order
          : null,
      filteredValue: columnFilters["driver.valueNegotiated"]
        ? [columnFilters["driver.valueNegotiated"]]
        : null,
      ...getColumnSearchProps(["driver", "valueNegotiated"], "money"),
      render: (value) =>
        `${(value || 0).toLocaleString("pt-br", { style: "currency", currency: "BRL" })}`,
      sorter: (a, b) =>
        (a.driver?.valueNegotiated || 0) - (b.driver?.valueNegotiated || 0),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      align: "center",
      sortOrder:
        sorterConfig.columnKey === "status" ? sorterConfig.order : null,
      filteredValue: columnFilters["status.describe"]
        ? columnFilters["status.describe"]
        : null,
      ...getStatusFilterProps(),
      render: (status) => {
        const describeText =
          status?.describe === "Pendente de contratação"
            ? "P.Contratação"
            : status?.describe;
        return (
          <Tooltip title={status?.describe}>
            <div>{renderStatusColor(describeText)}</div>
          </Tooltip>
        );
      },
      sorter: (a, b) =>
        (a.status?.describe || "").localeCompare(b.status?.describe || ""),
    },
    {
      title: "Ações",
      align: "center",
      key: "action",
      render: (_, record) => {
        const handleMenuClick = ({ key }) => {
          if (key === "queue") getDriversQueue(record.id);
          if (key === "delete") deleteEmb(record.id);
        };

        const menu = (
          <Menu
            onClick={handleMenuClick}
            style={{
              minWidth: 200,
              padding: "8px 0",
              borderRadius: 12,
              boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
            }}
          >
            <Menu.Item key="queue" style={menuItemStyle}>
              <Link
                to={`#${record.id}`}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  width: "100%",
                }}
              >
                <DriverBadge recordId={record.id} />
                Ver Fila
              </Link>
            </Menu.Item>

            <Menu.Item key="edit" style={menuItemStyle}>
              {user?.permissoes?.freight?.isEdit ||
                user?.permissoes?.frete?.editar ? (
                <Link
                  to={`/freightCopy/${record.id}`}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    width: "100%",
                  }}
                >
                  <EditOutlined style={{ fontSize: 18 }} />
                  Editar frete
                </Link>
              ) : (
                <Link
                  to={`/freightCopy/${record.id}`}
                  state={{ viewOnly: true }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    width: "100%",
                  }}
                >
                  <VisibilityIcon style={{ fontSize: 18 }} />
                  Visualizar frete
                </Link>
              )}
            </Menu.Item>

            <Menu.Item key="copy" style={menuItemStyle}>
              <Link
                to={`/freightCopy/${record.id}?copy=true`}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  width: "100%",
                }}
              >
                <CopyOutlined style={{ fontSize: 18 }} />
                Copiar
              </Link>
            </Menu.Item>

            <div
              style={{
                height: 1,
                background: "rgba(1,36,66,0.1)",
                margin: "6px 8px",
              }}
            />

            <Menu.Item
              key="delete"
              style={{
                ...menuItemStyle,
                color: "#ff0000",
              }}
            >
              {user?.permissoes?.freight?.isDelete ||
                user?.permissoes?.frete?.excluir ? (
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <DeleteOutlined style={{ fontSize: 18 }} />
                  Excluir frete
                </div>
              ) : null}
            </Menu.Item>
          </Menu>
        );

        return (
          <Dropdown overlay={menu} trigger={["click"]} placement="bottomRight">
            <Button
              type="text"
              icon={<SmallDashOutlined style={{ fontSize: 20 }} />}
            />
          </Dropdown>
        );
      },
    },
  ];

  // Aplica todos os filtros e ordenação aos dados
  const filteredAndSortedData = useMemo(() => {
    let filteredData = [...datas];

    // Aplica filtro de data
    if (dateRange && dateRange.length === 2) {
      const [startDate, endDate] = dateRange;
      filteredData = filteredData.filter((record) => {
        const dateColeta = getDateOnly(getDateColetaFromRecord(record));
        if (!dateColeta) return false;

        const start = startDate ? getDateOnly(startDate) : null;
        const end = endDate ? getDateOnly(endDate) : null;

        // Se ambas as datas são iguais, compara somente o dia
        if (start && end && start.getTime() === end.getTime()) {
          return dateColeta.getTime() === start.getTime();
        }

        // Somente data inicial definida: data em ou após startDate
        if (start && !end) {
          return dateColeta.getTime() >= start.getTime();
        }

        // Somente data final definida: data em ou antes de endDate
        if (!start && end) {
          return dateColeta.getTime() <= end.getTime();
        }

        // Intervalo completo entre startDate e endDate (inclusive)
        if (start && end) {
          return (
            dateColeta.getTime() >= start.getTime() &&
            dateColeta.getTime() <= end.getTime()
          );
        }

        return true;
      });
    }

    // Aplica filtros de colunas
    Object.keys(columnFilters).forEach((columnKey) => {
      const filterValue = columnFilters[columnKey];
      if (
        filterValue == null ||
        filterValue === "" ||
        (Array.isArray(filterValue) && filterValue.length === 0)
      )
        return;

      filteredData = filteredData.filter((record) => {
        // Filtro de status
        if (columnKey === "status.describe") {
          if (!record.status?.describe) return false;

          if (Array.isArray(filterValue) && filterValue.length > 0) {
            // Comparação case-insensitive e normalizada
            const recordStatus = record.status.describe.trim();
            return filterValue.some((status) => {
              const filterStatus = String(status).trim();
              return (
                recordStatus === filterStatus ||
                recordStatus.toLowerCase() === filterStatus.toLowerCase()
              );
            });
          }

          if (filterValue) {
            const recordStatus = String(record.status.describe).trim();
            const filterStatus = String(filterValue).trim();
            return (
              recordStatus === filterStatus ||
              recordStatus.toLowerCase() === filterStatus.toLowerCase()
            );
          }

          return false;
        }

        // Filtro de data já aplicado acima
        if (columnKey === "dateColeta") return true;

        const columnKeyArray = columnKey.split(".");
        let recordValue = record;

        for (const key of columnKeyArray) {
          recordValue = recordValue?.[key];
        }

        if (recordValue == null) return false;

        // Filtros com operadores (para valores monetários)
        if (
          typeof filterValue === "object" &&
          filterValue.operator &&
          filterValue.value !== undefined
        ) {
          const { operator, value: filterVal } = filterValue;
          const recordNum =
            typeof recordValue === "number"
              ? recordValue
              : moneyToInt(recordValue);
          const filterNum =
            typeof filterVal === "number" ? filterVal : moneyToInt(filterVal);

          switch (operator) {
            case "greaterThan":
              return recordNum > filterNum;
            case "greaterThanOrEqual":
              return recordNum >= filterNum;
            case "lessThan":
              return recordNum < filterNum;
            case "lessThanOrEqual":
              return recordNum <= filterNum;
            case "equals":
              return recordNum === filterNum;
            case "notEquals":
              return recordNum !== filterNum;
            default:
              return true;
          }
        }

        // Filtros simples de texto/número
        const isNumber = columnKey === "numberSerial";
        const isMoney =
          columnKey === "freight.valueFreightage" ||
          columnKey === "driver.valueNegotiated";

        if (isNumber) {
          return String(recordValue).includes(String(filterValue));
        } else if (isMoney) {
          const filterInt =
            typeof filterValue === "number"
              ? filterValue
              : moneyToInt(filterValue);
          const recordInt =
            typeof recordValue === "number"
              ? Math.round(recordValue)
              : moneyToInt(recordValue);
          return String(recordInt).includes(String(filterInt));
        } else {
          const normalizedFilter = filterValue
            .toString()
            .toLowerCase()
            .trim();

          if (columnKey === "firstDelivery.city") {
            const city = record?.firstDelivery?.city || "";
            const uf = record?.firstDelivery?.uf || "";

            const haystack = `${city} ${uf} ${city} - ${uf}`
              .toLowerCase()
              .replace(/\s+/g, " ")
              .trim();

            return haystack.includes(normalizedFilter);
          }

          if (columnKey === "lastDelivery.city") {
            const city = record?.lastDelivery?.city || "";
            const uf = record?.lastDelivery?.uf || "";

            const haystack = `${city} ${uf} ${city} - ${uf}`
              .toLowerCase()
              .replace(/\s+/g, " ")
              .trim();

            return haystack.includes(normalizedFilter);
          }

          return recordValue
            .toString()
            .toLowerCase()
            .includes(normalizedFilter);
        }
      });
    });

    // Aplica ordenação por prioridade de status
    const sortedData = filteredData.sort((a, b) => {
      const priorityA = statusPriority[a.status?.describe] || 5;
      const priorityB = statusPriority[b.status?.describe] || 5;
      return priorityA - priorityB;
    });

    return sortedData;
  }, [
    datas,
    dateRange,
    columnFilters,
    getDateColetaFromRecord,
    statusPriority,
  ]);

  return (
    <>
      <DialogFileDriver
        driversInFila={driversInFila}
        openModal={openModal}
        handleClose={handleClose}
      />

      <ModalFilter
        visible={openModalFilter}
        onClose={() => setOpenModalFilter(false)}
        onApply={handleApplyCustomFilter}
      />

      <YesOrNot
        open={open}
        handleClose={handleCloseYesOrNot}
        onResult={handleModalResult}
      />

      <Table
        columns={columns}
        dataSource={filteredAndSortedData}
        rowKey="id"
        loading={loading}
        onChange={(_pagination, _filters, sorter) => {
          if (sorter && sorter.columnKey && sorter.order) {
            setSorterConfig({
              columnKey: sorter.columnKey,
              order: sorter.order,
            });
          } else {
            setSorterConfig({ columnKey: null, order: null });
          }
        }}
        pagination={{
          current,
          pageSize,
          total: filteredAndSortedData.length,
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "50", "100"],
          hideOnSinglePage: false,
          onChange: (page, size) => {
            setCurrent(page);
            setPageSize(size);
          },
        }}
      />
    </>
  );
};

export default DataFreteTableCopy;
